
/**
 * Formate une date en français
 * @param {string|Date} date - Date à formater
 * @param {string} format - Format de sortie ('short', 'medium', 'long', 'relative')
 * @returns {string} Date formatée
 */
export const formatDate = (date, format = 'medium') => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return 'Date invalide';
  }

  const now = new Date();
  const diffTime = now - dateObj;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffTime / (1000 * 60));

  if (format === 'relative') {
    if (diffMinutes < 1) {
      return "À l'instant";
    } else if (diffMinutes < 60) {
      return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `Il y a ${weeks} semaine${weeks > 1 ? 's' : ''}`;
    } else {
      const months = Math.floor(diffDays / 30);
      return `Il y a ${months} mois`;
    }
  }

  const options = {
    short: {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    },
    medium: {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    },
    long: {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
  };

  return new Intl.DateTimeFormat('fr-FR', options[format] || options.medium).format(dateObj);
};

/**
 * @param {string} html - Texte HTML
 * @returns {string} Texte sans balises HTML
 */
export const stripHtml = (html) => {
  if (!html) return '';
  
  let decodedHtml = html
    .replace(/\\u003C/g, '<')
    .replace(/\\u003E/g, '>')
    .replace(/\\u0026/g, '&')
    .replace(/\\u0027/g, "'")
    .replace(/\\u0022/g, '"')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n');

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = decodedHtml;
  
  const textWithSpaces = tempDiv.textContent || tempDiv.innerText || '';
  
  return textWithSpaces.replace(/\s+/g, ' ').trim();
};

/**
 * @param {string} text - Texte à analyser
 * @param {number} wpm - Mots par minute (par défaut 200)
 * @returns {number} Temps de lecture en minutes
 */
export const estimateReadingTime = (text, wpm = 200) => {
  if (!text) return 0;
  const cleanText = stripHtml(text);
  const words = cleanText.split(/\s+/).filter(word => word.length > 0);
  const minutes = Math.ceil(words.length / wpm);
  return Math.max(1, minutes);
};

export const generatePlaceholderImage = (title = 'Article', id = '') => {
  return `https://via.placeholder.com/600x400/4F46E5/FFFFFF?text=${encodeURIComponent(title.substring(0, 30))}&font=Arial`;
};

/**
 * @param {string} content - Contenu HTML
 * @param {number} maxLength - Longueur maximale de l'extrait
 * @returns {string} Extrait nettoyé
 */
export const generateExcerpt = (content, maxLength = 150) => {
  if (!content) return '';
  
  const cleanContent = stripHtml(content);
  
  if (cleanContent.length <= maxLength) {
    return cleanContent;
  }
  
  const excerpt = cleanContent.substring(0, maxLength);
  const lastSpaceIndex = excerpt.lastIndexOf(' ');
  
  if (lastSpaceIndex > 0) {
    return excerpt.substring(0, lastSpaceIndex) + '...';
  }
  
  return excerpt + '...';
};

/**
 * @param {Object} article - Article de votre API
 * @returns {string|null} URL de l'image ou null
 */

export const extractFeaturedImage = (article) => article?.image || null;


/**
 * @param {Object} article - Article de votre API
 * @returns {string} URL complète de l'article
 */
export const getArticleUrl = (article) => {
  if (!article) return '#';
  
  if (article.postName) {
    return `/article/${article.postName}`;
  }
  
  if (article.slug) {
    return `/article/${article.slug}`;
  }
  
  if (article.ID || article.id) {
    return `/article/${article.ID || article.id}`;
  }
  
  return '#';
};

/**
 * Génère l'URL d'une catégorie
 * @param {Object} category - Catégorie
 * @returns {string} URL de la catégorie
 */
export const getCategoryUrl = (category) => {
  if (!category) return '#';
  
  if (category.slug) {
    return `/category/${category.slug}`;
  }
  
  if (category.id) {
    return `/category/${category.id}`;
  }
  
  return '#';
};

/**
 * @param {string} imageUrl - URL de l'image à valider
 * @returns {boolean} True si l'URL est valide, false sinon
 */
export const validateImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    console.log('❌ validateImageUrl: URL vide ou invalide:', imageUrl);
    return false;
  }
  
  console.log('🔍 validateImageUrl: Validation de:', imageUrl);
  
  // Vérifier les extensions d'image courantes
  const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i;
  
  // Test 1: Vérifier si c'est une URL complète valide
  try {
    const urlObj = new URL(imageUrl);
    const isValidExtension = imageExtensions.test(urlObj.pathname);
    console.log('✅ validateImageUrl: URL complète valide avec extension:', isValidExtension);
    return isValidExtension;
  } catch (urlError) {
    console.log('⚠️ validateImageUrl: Pas une URL complète, test comme chemin relatif');
  }
  
  // Test 2: Vérifier si c'est un chemin relatif valide
  if (imageUrl.startsWith('/') || imageUrl.startsWith('./')) {
    const isValidExtension = imageExtensions.test(imageUrl);
    console.log('📁 validateImageUrl: Chemin relatif avec extension valide:', isValidExtension);
    return isValidExtension;
  }
  
  // Test 3: Vérifier juste l'extension si c'est un nom de fichier
  const isValidExtension = imageExtensions.test(imageUrl);
  console.log('📄 validateImageUrl: Extension de fichier valide:', isValidExtension);
  return isValidExtension;
};

/**
 * 🆕 Fonction pour créer une URL d'image complète et valide
 * @param {string} imagePath - Chemin ou URL d'image
 * @param {string} baseUrl - URL de base (par défaut localhost:3001)
 * @returns {string} URL complète et valide
 */
export const createImageUrl = (imagePath, baseUrl = 'https://xibarubamback.onrender.com') => {
  if (!imagePath) {
    console.log('❌ createImageUrl: Pas de chemin d\'image fourni');
    return null;
  }
  
  // Si c'est déjà une URL complète, la retourner
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    console.log('✅ createImageUrl: URL complète détectée:', imagePath);
    return imagePath;
  }
  
  // Si ça commence par /uploads/, créer l'URL complète
  if (imagePath.startsWith('/uploads/')) {
    const fullUrl = `${baseUrl}${imagePath}`;
    console.log('🔗 createImageUrl: URL créée avec /uploads/:', fullUrl);
    return fullUrl;
  }
  
  // Sinon, ajouter /uploads/ au chemin
  const fullUrl = `${baseUrl}/uploads/${imagePath}`;
  console.log('🔗 createImageUrl: URL créée avec chemin simple:', fullUrl);
  return fullUrl;
};

/**
 * Nettoie et valide le titre d'un article
 * @param {string} title - Titre brut
 * @returns {string} Titre nettoyé
 */
export const cleanTitle = (title) => {
  if (!title) return 'Sans titre';
  
  // Nettoyer le HTML et les caractères spéciaux
  let cleanedTitle = stripHtml(title);
  
  // Décoder les entités HTML communes
  cleanedTitle = cleanedTitle
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  
  return cleanedTitle.trim() || 'Sans titre';
};

/**
 * 🆕 Teste si une image est accessible via une requête HEAD
 * @param {string} imageUrl - URL de l'image à tester
 * @returns {Promise<boolean>} True si l'image est accessible
 */
export const testImageAccessibility = async (imageUrl) => {
  if (!imageUrl) return false;
  
  try {
    console.log('🔍 testImageAccessibility: Test de:', imageUrl);
    const response = await fetch(imageUrl, { method: 'HEAD' });
    const isAccessible = response.ok;
    console.log(`${isAccessible ? '✅' : '❌'} testImageAccessibility: Image accessible:`, isAccessible);
    return isAccessible;
  } catch (error) {
    console.log('❌ testImageAccessibility: Erreur:', error.message);
    return false;
  }
};

/**
 * @param {string} imageUrl - URL à débugger
 * @returns {Object} Informations de debug
 */
export const debugImageUrl = (imageUrl) => {
  const debug = {
    originalUrl: imageUrl,
    isEmpty: !imageUrl,
    isString: typeof imageUrl === 'string',
    isHttpUrl: imageUrl?.startsWith('http'),
    isRelativePath: imageUrl?.startsWith('/'),
    hasImageExtension: false,
    finalUrl: null,
    isValid: false
  };
  
  if (imageUrl && typeof imageUrl === 'string') {
    debug.hasImageExtension = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(imageUrl);
    debug.finalUrl = createImageUrl(imageUrl);
    debug.isValid = validateImageUrl(imageUrl);
  }
  
  console.log('🔍 debugImageUrl:', debug);
  return debug;
};